/**
 * useWebOAuthCallback — gère le callback OAuth Google sur web globalement
 * (pas seulement quand LoginScreen est monté).
 *
 * Sur web en mode browse libre, l'utilisateur arrive sur /auth/callback?code=...
 * mais LoginScreen n'est pas dans le stack actif (on rend Home par défaut).
 * Du coup le useEffect de LoginScreen ne s'exécute jamais et le code OAuth
 * reste inutilisé. Ce hook DOIT être appelé au niveau de RootNavigator
 * (à l'intérieur de AuthProvider) pour s'exécuter au mount sur n'importe
 * quel écran.
 *
 * v1.12.117 — refonte web app + browse libre.
 */

import { useEffect } from 'react';
import { Platform, Alert } from 'react-native';
import { AWS_CONFIG } from '../config/aws_config';
import { useAuth } from '../context/AuthContext';

function decodeJwt(token) {
  try {
    const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 ? b64 + '='.repeat(4 - b64.length % 4) : b64;
    return JSON.parse(atob(pad));
  } catch { return {}; }
}

export function useWebOAuthCallback() {
  const { signInWithGoogle } = useAuth();

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    const code = url.searchParams.get('code');
    const errMsg = url.searchParams.get('error_description') || url.searchParams.get('error');
    // N'agir que si on est explicitement sur /auth/callback
    if (!url.pathname.startsWith('/auth/callback')) return;
    if (errMsg) {
      Alert.alert('Connexion impossible', errMsg);
      window.history.replaceState({}, '', '/');
      return;
    }
    if (!code) return;

    let cancelled = false;
    (async () => {
      try {
        const domain = AWS_CONFIG.oauth?.domain;
        const clientId = AWS_CONFIG.userPoolClientId;
        const redirectUri = `${window.location.origin}/auth/callback`;
        const tokenRes = await fetch(`https://${domain}/oauth2/token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: 'grant_type=authorization_code' +
            '&client_id=' + encodeURIComponent(clientId) +
            '&code=' + encodeURIComponent(code) +
            '&redirect_uri=' + encodeURIComponent(redirectUri),
        });
        const tokens = await tokenRes.json();
        if (cancelled) return;
        if (tokens.error) throw new Error(tokens.error_description || tokens.error);
        const idPayload = decodeJwt(tokens.id_token);
        let userInfo = {};
        try {
          const uiRes = await fetch(`https://${domain}/oauth2/userInfo`, {
            headers: { Authorization: `Bearer ${tokens.access_token}` },
          });
          if (uiRes.ok) userInfo = await uiRes.json();
        } catch {}
        if (cancelled) return;
        const name = userInfo.name || idPayload.name || idPayload.email || '';
        const email = userInfo.email || idPayload.email || '';
        const picture = userInfo.picture || idPayload.picture || null;
        const firstName = userInfo.given_name || idPayload.given_name || name.split(' ')[0] || '';
        const lastName = userInfo.family_name || idPayload.family_name || name.split(' ').slice(1).join(' ') || '';
        await signInWithGoogle({
          accessToken: tokens.access_token,
          idToken: tokens.id_token,
          refreshToken: tokens.refresh_token,
          name, email, picture, firstName, lastName,
        });
        // Clean l'URL (enlève ?code=)
        window.history.replaceState({}, '', '/');
      } catch (e) {
        if (!cancelled) {
          Alert.alert('Connexion impossible', e?.message || 'Erreur OAuth');
          window.history.replaceState({}, '', '/');
        }
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export default useWebOAuthCallback;
